import React from "react";
import { Link } from "react-router-dom";

// Auto Glass Resources — article registry.
// Each article: { slug, title, excerpt, category, updated, seoTitle, seoDescription, body }
export const RESOURCES = [
  {
    slug: "how-to-find-correct-replacement-side-mirror-glass",
    title: "How to Find the Correct Replacement Side Mirror Glass for Your Vehicle",
    category: "Buying Guide",
    updated: "June 2026",
    excerpt:
      "A step-by-step guide to identifying the right replacement side mirror glass — from your vehicle's year, make, model and trim to driver/passenger side and mirror features.",
    seoTitle:
      "How to Find the Correct Replacement Side Mirror Glass | Lecis Auto Glass",
    seoDescription:
      "Identify the correct replacement side mirror glass for your vehicle: year, make, model, trim, driver vs. passenger side, mirror features, and confirming fitment.",
    body: (
      <>
        <p>
          Replacing a side mirror sounds simple until you start shopping and realize a single vehicle
          can have several different mirror glass variations. The correct piece depends on more than the
          badge on your car — it comes down to your exact configuration and which features your mirror
          was built with. This guide walks through how to narrow it down with confidence before you
          order.
        </p>

        <h2>Start with your exact vehicle</h2>
        <p>
          The single most important step is matching the glass to your specific vehicle. Two cars that
          look identical from the curb can use different mirror glass depending on the model year and
          how they were optioned. Gather the following before you begin:
        </p>
        <ul>
          <li>Model year</li>
          <li>Make</li>
          <li>Model</li>
          <li>Trim or sub-model, if you know it</li>
        </ul>

        <h3>Why trim matters</h3>
        <p>
          Trim levels sometimes change which features a mirror carries, and features can change the glass
          itself. If you're unsure of your trim, it's usually printed on your original window sticker or
          available from your dealership using your vehicle information. When trim is uncertain, it's
          better to confirm fitment with a person than to guess.
        </p>

        <h2>Driver side vs. passenger side</h2>
        <p>
          Side mirror glass is side-specific. The driver-side and passenger-side pieces are shaped and
          curved differently, and they are not interchangeable. When you shop, you'll typically choose:
        </p>
        <ul>
          <li>
            <strong>Left / driver side</strong> — the mirror on the side where the steering wheel is on a
            U.S.-market vehicle.
          </li>
          <li>
            <strong>Right / passenger side</strong> — the opposite side, often with a wider field of view.
          </li>
          <li>
            <strong>Pair</strong> — both pieces together, which many shoppers choose when both mirrors are
            aging or one has already been replaced.
          </li>
        </ul>
        <p>
          If you're only replacing one mirror, double-check which side you actually need before ordering.
          You can browse both options on our{" "}
          <Link to="/shop/side-mirror">side mirror category page</Link>.
        </p>

        <h2>Identify the features your mirror has</h2>
        <p>
          Modern mirrors can include electronics and coatings that must be matched. Look closely at your
          existing mirror and, where possible, your original documentation to determine which of the
          following apply:
        </p>
        <h3>Heated glass</h3>
        <p>
          Heated mirrors have an element behind the glass to clear fog and frost. If your car has a
          defrost feature that also warms the mirrors, you'll generally want heated glass so the function
          continues to work.
        </p>
        <h3>Blind-spot indicators</h3>
        <p>
          Some mirrors include a warning light or icon within the glass that illuminates when a vehicle is
          detected alongside you. This is a feature of the glass assembly and needs to be matched.
        </p>
        <h3>Auto-dimming</h3>
        <p>
          Auto-dimming glass darkens automatically to reduce glare from headlights behind you. It is a
          different piece from standard glass.
        </p>
        <h3>Integrated cameras or additional hardware</h3>
        <p>
          Certain vehicles route camera or sensor hardware near the mirror. If your mirror interacts with
          driver-assistance features, note that when confirming the correct part.
        </p>
        <p>
          The goal is simple: the replacement should carry the same features your original glass had. If
          your existing mirror is heated, choose heated glass; if it has a blind-spot indicator, match
          that too.
        </p>

        <h2>Glass-only vs. full assembly</h2>
        <p>
          Depending on the damage, you may need only the mirror glass, or you may need a larger portion of
          the mirror. If the housing, motor or mounting is intact and only the reflective glass is broken,
          replacement glass is often the practical choice. When you're unsure how much of the mirror is
          affected, describe the damage to our team and we'll help you sort out what you actually need.
        </p>

        <h2>Use your VIN and any part markings</h2>
        <p>
          Your Vehicle Identification Number (VIN) captures how your car was built and can help confirm
          your configuration. You may also find markings etched or printed on the back of your original
          mirror glass. These details are useful reference points when verifying fitment — share them with
          us if you have them, and we'll cross-check against the correct listing.
        </p>

        <h2>A note on brands and compatibility</h2>
        <p>
          When you shop, it helps to separate two ideas. Your <em>vehicle's</em> make and model describe
          which parts are <em>compatible</em> with your car. The <em>product</em> you buy is a listing sold
          by Lecis Auto Glass, offered in OEM-style and quality aftermarket options. Vehicle names on a
          listing describe compatibility only — they don't mean the item is a vehicle-manufacturer branded
          part unless the listing explicitly says so. If you'd like to understand the difference in more
          depth, see our guide on{" "}
          <Link to="/resources/oem-vs-aftermarket-auto-glass">OEM vs. aftermarket auto glass</Link>.
        </p>

        <h2>When in doubt, confirm fitment</h2>
        <p>
          Auto glass is fragile and fitment-sensitive, and the most common ordering mistakes come from a
          missed trim or feature. If anything is unclear, don't guess. Our team verifies fitment before you
          buy — just reach out through our{" "}
          <Link to="/contact">Contact &amp; Fitment Help</Link> page with your year, make, model, trim and a
          description of your mirror's features. You can also start from your vehicle using our{" "}
          <Link to="/find-my-vehicle">Find My Vehicle</Link> tool.
        </p>

        <h2>Quick checklist before you order</h2>
        <ul>
          <li>Confirmed year, make, model and (ideally) trim</li>
          <li>Correct side — left/driver, right/passenger, or a pair</li>
          <li>Matched features — heated, blind-spot indicator, auto-dimming, etc.</li>
          <li>Checked your VIN or original glass markings if available</li>
          <li>Reached out for fitment help if anything is uncertain</li>
        </ul>
        <p>
          Taking a few minutes on these steps saves the hassle of a return later. And if you do need to
          send something back, our <Link to="/return-policy">Return Policy</Link> covers eligible items,
          with return shipping paid by Lecis Auto Glass on approved returns.
        </p>
      </>
    ),
  },
  {
    slug: "oem-vs-aftermarket-auto-glass",
    title: "OEM vs. Aftermarket Auto Glass: What's the Difference?",
    category: "Buying Guide",
    updated: "June 2026",
    excerpt:
      "Understand the difference between OEM, OEM-style and aftermarket auto glass, what the terms mean, and how to choose the right option for side mirrors, windshields and backglass.",
    seoTitle: "OEM vs. Aftermarket Auto Glass: What's the Difference? | Lecis Auto Glass",
    seoDescription:
      "A clear explanation of OEM, OEM-style and aftermarket auto glass terminology, the trade-offs of each, and how to choose the right option for your vehicle.",
    body: (
      <>
        <p>
          When you shop for replacement glass, you'll run into the terms "OEM" and "aftermarket" quickly.
          They describe where a part comes from and how it's positioned in the market — not automatically
          whether one is "good" and the other "bad." Understanding what each term means helps you choose
          the option that fits your priorities and budget.
        </p>

        <h2>What does OEM mean?</h2>
        <p>
          OEM stands for Original Equipment Manufacturer. In everyday use, "OEM" refers to a part made to
          the original manufacturer's specification — the type of part your vehicle may have been built
          with. Shoppers often choose OEM when they want the closest match to what came on the car
          originally.
        </p>
        <h3>OEM vs. "OEM-style"</h3>
        <p>
          You'll also see the phrase "OEM-style." This describes a part designed to follow the form and
          function of the original piece. The distinction matters, so read each listing carefully: the
          exact designation for any given item is whatever that product page states, and you shouldn't
          assume a part is a vehicle-manufacturer branded component unless the listing says so explicitly.
        </p>

        <h2>What does aftermarket mean?</h2>
        <p>
          Aftermarket parts are produced by companies other than the original vehicle manufacturer. Quality
          across the aftermarket varies widely — from budget pieces to well-made parts intended to match
          the fit and function of the original. A quality aftermarket option can be a sensible choice,
          especially when availability or cost is a factor.
        </p>

        <h2>How the terms relate to "brand"</h2>
        <p>
          This is where shoppers sometimes get confused, so it's worth being precise. There are two
          separate ideas at play:
        </p>
        <ul>
          <li>
            <strong>Vehicle compatibility</strong> — your car's make and model tell you which parts fit.
            When a listing names a vehicle, it's describing what the part is compatible with.
          </li>
          <li>
            <strong>Product listing brand</strong> — the item you purchase is sold by Lecis Auto Glass and
            may be an OEM-style or aftermarket option. The vehicle name on a compatibility list does not,
            by itself, mean the item is a vehicle-manufacturer branded product.
          </li>
        </ul>
        <p>
          In short: naming a vehicle describes fit, not the product's manufacturer. Lecis Auto Glass does
          not manufacture vehicle-brand OEM products; we source and sell replacement glass and mirror
          products, and each listing states what it is.
        </p>

        <h2>How to choose between them</h2>
        <h3>Consider your priorities</h3>
        <ul>
          <li>
            <strong>Closest-to-original match:</strong> if that's your top priority, look for an OEM or
            OEM-style option and confirm the features match your vehicle.
          </li>
          <li>
            <strong>Value and availability:</strong> a quality aftermarket option can offer a good balance
            of cost and function, and is sometimes available when an original-spec piece is hard to source.
          </li>
          <li>
            <strong>Features:</strong> whichever route you choose, the part must match the features your
            vehicle uses — heated glass, blind-spot indicators, auto-dimming and similar. Matching features
            is more important than the OEM/aftermarket label alone.
          </li>
        </ul>

        <h2>Fit is what ultimately matters</h2>
        <p>
          Whether you go OEM-style or aftermarket, the part still has to fit your exact vehicle and carry
          the correct features. That's why we recommend confirming fitment before you order. Our team can
          help you match the right option to your year, make, model and trim through our{" "}
          <Link to="/contact">Contact &amp; Fitment Help</Link> page. For mirror-specific guidance, see{" "}
          <Link to="/resources/how-to-find-correct-replacement-side-mirror-glass">
            how to find the correct replacement side mirror glass
          </Link>
          .
        </p>

        <h2>Where you can shop</h2>
        <p>
          Lecis Auto Glass focuses on side mirrors, windshields, backglass and sunroof glass — including
          hard-to-source pieces for exotic, luxury and EV vehicles. You can browse by category:
        </p>
        <ul>
          <li>
            <Link to="/shop/side-mirror">Side mirrors</Link>
          </li>
          <li>
            <Link to="/shop/windshield">Windshields</Link>
          </li>
          <li>
            <Link to="/shop/backglass">Backglass</Link>
          </li>
          <li>
            <Link to="/shop/sunroof">Sunroof glass</Link>
          </li>
        </ul>

        <h2>Buy with confidence</h2>
        <p>
          Because both OEM-style and aftermarket options are available, the best choice is the one that
          matches your vehicle and your priorities. If you order and the item isn't right, eligible products
          can be returned within 30 days under our <Link to="/return-policy">Return Policy</Link>, with
          return shipping paid by Lecis Auto Glass on approved returns. When you're not sure which option to
          pick, ask us first — a quick fitment check is the easiest way to get it right the first time.
        </p>
      </>
    ),
  },
  {
    slug: "sourcing-low-volume-ev-glass-united-states",
    title: "Sourcing Low-Volume EV Glass in the United States",
    category: "EV Glass",
    updated: "June 2026",
    excerpt:
      "Why glass for lower-volume EV and specialty vehicles can be hard to find in the U.S., what makes sourcing challenging, and how to track down the right part.",
    seoTitle: "Sourcing Low-Volume EV Glass in the United States | Lecis Auto Glass",
    seoDescription:
      "Glass for lower-volume EV and specialty vehicles is hard to source in the U.S. Learn why availability is limited and how to find the right replacement glass.",
    body: (
      <>
        <p>
          Electric vehicles have expanded the range of makes and models on the road, and with them a
          growing number of lower-volume and specialty vehicles. When one of these cars needs replacement
          glass, owners often discover that finding the right piece is harder than expected. This guide
          explains why low-volume EV glass can be difficult to source in the United States and how to
          approach the search.
        </p>

        <h2>Why low-volume glass is hard to find</h2>
        <h3>Fewer units on the road</h3>
        <p>
          High-volume vehicles generate steady demand, so replacement parts are stocked widely. When a
          model is produced in smaller numbers, fewer sellers carry glass for it, and the parts that do
          exist can be spread thin across the market. That's the core of the challenge: limited demand
          leads to limited, scattered supply.
        </p>
        <h3>More configurations, more variation</h3>
        <p>
          EVs frequently bundle sensors, cameras and driver-assistance hardware around the glass. Combined
          with trim and model-year differences, this can create several variations of what looks like a
          single part. Matching the right configuration takes more care than with a common, long-running
          model.
        </p>
        <h3>Fragile, oversized items</h3>
        <p>
          Windshields, backglass and large panels are fragile and often oversized, which limits how many
          suppliers are willing to stock and ship them. That further narrows the pool of sources for
          specialty vehicles.
        </p>

        <h2>What to gather before you search</h2>
        <p>
          The same fundamentals that apply to any glass search matter even more for low-volume vehicles,
          because there's less room for error:
        </p>
        <ul>
          <li>Model year, make and model</li>
          <li>Trim or sub-model, if known</li>
          <li>Your VIN, which reflects how the vehicle was built</li>
          <li>
            The features tied to the glass — heated elements, camera or sensor mounts, coatings, and
            similar
          </li>
        </ul>
        <p>
          Having these ready shortens the search and reduces the chance of ordering a piece that doesn't
          match your configuration. For a broader walkthrough of matching features, see our guide on{" "}
          <Link to="/resources/how-to-find-correct-replacement-side-mirror-glass">
            finding the correct replacement mirror glass
          </Link>
          , which covers the same fitment principles that apply to other glass.
        </p>

        <h2>Understanding your options</h2>
        <p>
          For specialty and EV vehicles, you may find both OEM-style and quality aftermarket options,
          depending on the part and availability. Neither label guarantees fit on its own — the part still
          has to match your exact vehicle and its features. If you'd like a plain-language explanation of
          the terminology, our guide on{" "}
          <Link to="/resources/oem-vs-aftermarket-auto-glass">OEM vs. aftermarket auto glass</Link>{" "}
          breaks it down.
        </p>
        <p>
          It's also worth keeping the distinction between compatibility and product brand in mind. Listing
          a vehicle's name describes what a part fits — it does not mean the item is a
          vehicle-manufacturer branded product. Lecis Auto Glass sources and sells replacement glass; each
          listing states what the item is.
        </p>

        <h2>How Lecis Auto Glass approaches specialty sourcing</h2>
        <p>
          Lecis Auto Glass focuses on exactly the kind of parts that many suppliers won't handle —
          hard-to-source side mirrors, windshields, backglass and sunroof glass for exotic, luxury and EV
          vehicles. That focus is the reason we exist: to help owners of lower-volume vehicles find glass
          that would otherwise be difficult to track down.
        </p>
        <p>
          If the exact piece you need isn't listed on the site yet, that doesn't necessarily mean it's
          unavailable. Reach out through our <Link to="/contact">Contact &amp; Fitment Help</Link> page with
          your vehicle details and a description of the glass, and we'll help you confirm fitment and look
          into sourcing options. You can also explore our <Link to="/ev-glass">EV Glass</Link> page and
          browse by <Link to="/brands">brand</Link> to see what's already available.
        </p>

        <h2>Shipping considerations for large EV glass</h2>
        <p>
          Because windshields and backglass are oversized and fragile, they typically ship via freight
          rather than standard ground service, which can add transit time. When your shipment arrives,
          inspect it carefully before signing, and report any transit damage promptly. The full details are
          in our <Link to="/shipping-policy">Shipping Policy</Link>, and our{" "}
          <Link to="/faq">FAQ</Link> answers common questions about delivery and returns.
        </p>

        <h2>The bottom line</h2>
        <p>
          Low-volume EV glass is harder to source simply because there's less of it and more variation to
          match. The path to success is the same every time: gather your vehicle details, confirm the
          configuration and features, and verify fitment before you buy. When a part is scarce or you're
          not certain what you need, ask — our team is here to help you find the right glass for a vehicle
          the rest of the market tends to overlook.
        </p>
      </>
    ),
  },
  {
    slug: "buying-replacement-windshield-online",
    title: "Buying a Replacement Windshield Online: What You Need to Know Before Ordering",
    category: "Buying Guide",
    updated: "June 2026",
    excerpt:
      "What to check before you buy a replacement windshield online — confirming your exact vehicle, matching sensors and features, understanding shipping, and getting fitment right the first time.",
    seoTitle: "Buying a Replacement Windshield Online: What to Know | Lecis Auto Glass",
    seoDescription:
      "Before ordering a windshield online, confirm your exact vehicle, match features like sensors and heating, and understand how freight shipping works.",
    body: (
      <>
        <p>
          Buying a windshield online can save time and open up access to glass that local shops may not
          stock — but a windshield is a large, fragile, fitment-sensitive part, so a little homework up
          front prevents headaches later. This guide covers what to confirm before you place an order.
        </p>

        <h2>Confirm your exact vehicle first</h2>
        <p>
          A windshield is matched to a specific vehicle configuration. Before shopping, gather your model
          year, make, model and — importantly — your trim or sub-model. Vehicles that look the same can use
          different windshields depending on how they were optioned, so the more precisely you can describe
          your vehicle, the better.
        </p>
        <h3>Why trim and options matter</h3>
        <p>
          Trim and factory options often determine which features the windshield carries. If you're not
          certain of your trim, your original window sticker or your dealership (using your vehicle
          information) can help confirm it. When it's unclear, it's far better to verify than to assume.
        </p>

        <h2>Match the features your windshield has</h2>
        <p>
          Modern windshields can integrate technology and coatings that must be matched on the replacement.
          Examine your current glass and documentation for features such as:
        </p>
        <ul>
          <li>A camera or sensor housing mounted near the top center (often tied to driver-assistance features)</li>
          <li>A rain or light sensor</li>
          <li>Heating elements or a de-icing zone, for example near the wiper park area</li>
          <li>An acoustic or laminated layer for noise reduction</li>
          <li>A heads-up display (HUD) area</li>
          <li>A tinted shade band along the top edge</li>
        </ul>
        <p>
          The replacement should carry the same features as your original. If your windshield supports
          driver-assistance cameras, note that the glass and any related calibration are part of getting the
          job done correctly. We don't perform installation or calibration ourselves — more on that below —
          but matching the right glass is the first step. Browse options on our{" "}
          <Link to="/shop/windshield">windshield category page</Link>.
        </p>

        <h2>OEM-style vs. aftermarket</h2>
        <p>
          You'll see both OEM-style and quality aftermarket windshields depending on the vehicle and
          availability. Neither label guarantees fit on its own — the glass still has to match your exact
          vehicle and features. For a plain-language explanation of the terms, see our guide on{" "}
          <Link to="/resources/oem-vs-aftermarket-auto-glass">OEM vs. aftermarket auto glass</Link>.
        </p>

        <h2>Compatibility is not the same as brand</h2>
        <p>
          When a listing names your vehicle, it's describing <em>compatibility</em> — what the glass fits.
          The product itself is sold by Lecis Auto Glass and may be an OEM-style or aftermarket option.
          Naming a vehicle does not mean the item is a vehicle-manufacturer branded part, and Lecis Auto
          Glass does not manufacture vehicle-brand OEM products. Each listing states what the item is.
        </p>

        <h2>How windshields ship</h2>
        <p>
          Because windshields are oversized and fragile, they typically ship via freight (LTL) carriers
          rather than standard parcel service. Freight deliveries are usually curbside and may require a
          signature, and they can take additional transit time compared with ground shipping. For a deeper
          look at how glass travels safely, see{" "}
          <Link to="/resources/how-automotive-glass-shipped-safely">
            how automotive glass is shipped safely across the United States
          </Link>
          , and check our <Link to="/shipping-policy">Shipping Policy</Link> for current processing and
          delivery expectations.
        </p>

        <h2>Inspect on delivery</h2>
        <p>
          Always inspect a windshield shipment before signing. Note any visible damage on the carrier's
          delivery receipt and photograph the packaging and glass. If you find transit damage, report it
          promptly to <a href="mailto:support@lecisautoglass.com">support@lecisautoglass.com</a> with your
          order number and photos so we can arrange a replacement or refund for verified damage.
        </p>

        <h2>Returns and getting it right</h2>
        <p>
          Eligible items can be returned within 30 days if they're unused, uninstalled and in original,
          resalable condition — see our <Link to="/return-policy">Return Policy</Link> for the details, and
          note that Lecis Auto Glass covers return shipping on approved returns. The simplest way to avoid a
          return is to confirm fitment before you buy.
        </p>

        <h2>A note on installation</h2>
        <p>
          Lecis Auto Glass supplies replacement glass; we do not perform installation. Windshield
          replacement — and any related driver-assistance recalibration your vehicle may require — should be
          handled by a qualified professional. If you have questions about what your vehicle needs, we're
          happy to help you sort out the glass side of it.
        </p>

        <h2>Before you order — quick checklist</h2>
        <ul>
          <li>Confirmed year, make, model and trim</li>
          <li>Matched features (sensors, camera, heating, acoustic layer, HUD, shade band)</li>
          <li>Reviewed shipping expectations for oversized freight</li>
          <li>Planned professional installation and any needed recalibration</li>
          <li>Reached out for fitment help if anything is uncertain</li>
        </ul>
        <p>
          If you'd like a second set of eyes before ordering, send your vehicle details through our{" "}
          <Link to="/contact">Contact &amp; Fitment Help</Link> page or start from your vehicle with{" "}
          <Link to="/find-my-vehicle">Find My Vehicle</Link>. More common questions are answered in our{" "}
          <Link to="/faq">FAQ</Link>.
        </p>
      </>
    ),
  },
  {
    slug: "how-to-identify-auto-glass-part-number",
    title: "How to Identify an Auto Glass Part Number",
    category: "How-To",
    updated: "June 2026",
    excerpt:
      "Where to look for markings on your existing auto glass, how a part number differs from your vehicle details, and how to use your VIN and photos to confirm the right replacement.",
    seoTitle: "How to Identify an Auto Glass Part Number | Lecis Auto Glass",
    seoDescription:
      "Where to find markings on your auto glass, how part numbers differ from vehicle details, and how to use your VIN and photos to confirm the right replacement.",
    body: (
      <>
        <p>
          When you're trying to order the right replacement glass, a part number or marking on your existing
          piece can be a helpful clue. But markings vary, and they're only one part of confirming fitment.
          This guide explains where to look, what the information does and doesn't tell you, and how to put
          it together with your vehicle details.
        </p>

        <h2>Where markings usually appear</h2>
        <p>
          Different glass pieces carry markings in different places. Common spots to check include:
        </p>
        <ul>
          <li>
            <strong>Windshields and larger glass:</strong> markings are often etched or printed in a lower
            corner of the glass.
          </li>
          <li>
            <strong>Side mirror glass:</strong> look on the back of the reflective glass or on the backing
            plate, where numbers or codes are sometimes present.
          </li>
          <li>
            <strong>Packaging and labels:</strong> if you still have the original box or a label from a prior
            replacement, it may list a reference number.
          </li>
        </ul>
        <p>
          Not every piece has an obvious, readable number, and formats differ from one source to another, so
          don't be discouraged if what you find looks unfamiliar.
        </p>

        <h2>What a part number does — and doesn't — tell you</h2>
        <p>
          A marking on the glass can help a knowledgeable seller cross-reference the correct replacement, but
          on its own it isn't a guarantee of fit. It should be combined with your vehicle details rather than
          used in isolation. Treat any number you find as a useful reference point to share, not the final
          word.
        </p>

        <h2>Start with your vehicle details</h2>
        <p>
          The foundation of any glass search is your exact vehicle: model year, make, model and trim. These
          confirm compatibility, and they're often more reliable than a partial or worn marking. If you're
          replacing a side mirror, our guide on{" "}
          <Link to="/resources/how-to-find-correct-replacement-side-mirror-glass">
            finding the correct replacement mirror glass
          </Link>{" "}
          walks through matching side and features step by step.
        </p>

        <h2>Use your VIN</h2>
        <p>
          Your Vehicle Identification Number (VIN) reflects how your vehicle was built and can help confirm
          your configuration when a marking is missing or unclear. Having your VIN ready makes it easier for
          our team to match the correct listing.
        </p>

        <h2>Photos make it easier</h2>
        <p>
          Clear photos of any markings — plus the feature side of a mirror or the sensor area of a windshield
          — often tell us more than a transcribed number alone. If a code is hard to read, a well-lit,
          close-up photo helps avoid transcription mistakes.
        </p>

        <h2>Numbers, compatibility and brand</h2>
        <p>
          It's worth keeping the terminology straight. A marking or reference number helps identify the
          <em> part</em>; your vehicle's name describes <em>compatibility</em>; and the <em>product</em> you
          buy is sold by Lecis Auto Glass as an OEM-style or aftermarket option. A vehicle name on a listing
          does not, by itself, mean the item is a vehicle-manufacturer branded product. If the OEM/aftermarket
          distinction is new to you, our{" "}
          <Link to="/resources/oem-vs-aftermarket-auto-glass">OEM vs. aftermarket guide</Link> explains it.
        </p>

        <h2>When markings are missing or unclear</h2>
        <p>
          If you can't find a number, or what you find is worn or ambiguous, don't guess your way into an
          order. Send us your vehicle details, your VIN if you have it, and photos of the glass through our{" "}
          <Link to="/contact">Contact &amp; Fitment Help</Link> page, and we'll help confirm the correct
          replacement. You can also start from your vehicle using{" "}
          <Link to="/find-my-vehicle">Find My Vehicle</Link> and browse{" "}
          <Link to="/shop/side-mirror">side mirrors</Link> or{" "}
          <Link to="/shop/windshield">windshields</Link> directly.
        </p>

        <h2>The takeaway</h2>
        <p>
          A part marking is a helpful clue, not a complete answer. Pair it with your year, make, model, trim
          and VIN, add photos when you can, and confirm fitment before ordering. When in doubt, ask — a quick
          check now is easier than a return later, and our <Link to="/faq">FAQ</Link> covers many common
          questions.
        </p>
      </>
    ),
  },
  {
    slug: "how-automotive-glass-shipped-safely",
    title: "How Automotive Glass Is Shipped Safely Across the United States",
    category: "Shipping",
    updated: "June 2026",
    excerpt:
      "How fragile, oversized auto glass travels safely — parcel vs. freight shipping, packaging, curbside delivery expectations, and what to do the moment your shipment arrives.",
    seoTitle: "How Automotive Glass Is Shipped Safely Across the U.S. | Lecis Auto Glass",
    seoDescription:
      "How fragile, oversized auto glass ships safely across the U.S.: parcel vs. freight, packaging, curbside delivery, and inspecting your shipment on arrival.",
    body: (
      <>
        <p>
          Auto glass is one of the more challenging things to ship: it's fragile, sometimes oversized, and it
          has to arrive intact to be usable. Understanding how glass travels — and what your role is when it
          arrives — helps set the right expectations and protects your order.
        </p>

        <h2>Why glass shipping is different</h2>
        <p>
          Unlike most parts, glass can't flex or absorb impact. Larger pieces like windshields and backglass
          are both fragile and oversized, which changes how they're packaged and which carriers handle them.
          That's why glass shipping often looks different from ordering a typical small part online.
        </p>

        <h2>Small items vs. large items</h2>
        <h3>Parcel-friendly items</h3>
        <p>
          Smaller pieces such as side mirror glass can often ship via standard parcel carriers, protected with
          appropriate packaging. These generally move on standard ground timelines.
        </p>
        <h3>Oversized, freight-only items</h3>
        <p>
          Windshields, backglass and other large panels are typically classified as oversized and ship via
          freight (LTL) carriers. Freight handling is designed to protect large, fragile items, but it works
          differently from parcel delivery — which is worth knowing before your shipment arrives.
        </p>

        <h2>Packaging built for fragility</h2>
        <p>
          Glass is packaged with protection intended to guard the edges and surface during transit. Larger
          pieces may be crated or specially braced. The goal is simple: keep the glass stable and cushioned so
          it survives the trip. When your item arrives, that packaging is also your first checkpoint for
          spotting any handling damage.
        </p>

        <h2>What to expect from freight delivery</h2>
        <p>
          Freight deliveries differ from a package left at your door. In general, you can expect:
        </p>
        <ul>
          <li>Curbside delivery rather than to-the-door or inside placement</li>
          <li>A possible appointment or notification window from the carrier</li>
          <li>A signature requirement on receipt</li>
          <li>Additional transit time compared with standard ground service</li>
        </ul>
        <p>
          For current processing and delivery timeframes, and details on where we ship, refer to our{" "}
          <Link to="/shipping-policy">Shipping Policy</Link> — it's the source of truth for those specifics.
          In general, we ship across the contiguous United States, and some destinations (such as Alaska,
          Hawaii, U.S. territories, P.O. boxes and international addresses) may be limited or unavailable.
        </p>

        <h2>Inspect your shipment on arrival</h2>
        <p>
          This step matters most. Before you sign for a freight delivery, look over the packaging and the
          glass:
        </p>
        <ul>
          <li>Note any visible damage on the carrier's delivery receipt before signing.</li>
          <li>Photograph the packaging and the product, especially anything that looks compromised.</li>
          <li>Keep all packaging until you've confirmed the item is intact.</li>
        </ul>
        <p>
          If you discover transit damage, report it promptly to{" "}
          <a href="mailto:support@lecisautoglass.com">support@lecisautoglass.com</a> with your order number
          and photos. For verified shipping damage, we'll arrange a replacement or refund. Reporting quickly,
          with documentation, makes the process smoother.
        </p>

        <h2>Returns and damaged items</h2>
        <p>
          Our <Link to="/return-policy">Return Policy</Link> covers eligible items returned within 30 days in
          unused, uninstalled, resalable condition, with return shipping covered by Lecis Auto Glass on
          approved returns. Damage discovered on delivery is handled through the transit-damage process above
          rather than a standard return. Our <Link to="/faq">FAQ</Link> answers common delivery and return
          questions.
        </p>

        <h2>Ordering large glass with confidence</h2>
        <p>
          Because oversized glass ships freight and can't be rushed the way a small parcel can, confirming
          fitment before you order is especially valuable — it avoids sending a large, fragile item back and
          forth. If you're buying a windshield, our guide on{" "}
          <Link to="/resources/buying-replacement-windshield-online">
            buying a replacement windshield online
          </Link>{" "}
          covers what to check first, and you can browse{" "}
          <Link to="/shop/windshield">windshields</Link> and{" "}
          <Link to="/shop/backglass">backglass</Link> by category. Questions? Reach us through{" "}
          <Link to="/contact">Contact &amp; Fitment Help</Link>.
        </p>
      </>
    ),
  },
];

export function getResource(slug) {
  return RESOURCES.find((r) => r.slug === slug) || null;
}
